// Statutes — public corpus search surface (Phase 9 item 15).
//
// Three retrieval modes (keyword / semantic / hybrid) over the three Texas
// corpus sources (Labor Code / 28 TAC / Insurance Code) with a source
// filter dropdown. Available to every authenticated user — reading the
// law is not a privileged operation (SPEC §3.11).

import { useEffect, useState } from "react";

import { corpus } from "../lib/api";
import type { CorpusHit, CorpusSource } from "../lib/types";

type Mode = "hybrid" | "keyword" | "semantic";

export function StatutesPage(): JSX.Element {
  const [sources, setSources] = useState<CorpusSource[]>([]);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<Mode>("hybrid");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [hits, setHits] = useState<CorpusHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    corpus
      .sources()
      .then((r) => setSources(r.sources))
      .catch((e: Error) => setError(e.message));
  }, []);

  const runSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await corpus.search(mode, {
        query,
        source_filter: sourceFilter || null,
        limit: 25,
      });
      setHits(resp.hits);
    } catch (err) {
      setError((err as Error).message);
      setHits([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Statutes</h1>
        <p className="text-sm text-slate-600">
          Search the Texas Labor Code, 28 TAC (DWC rules), and Texas Insurance Code Chapter 1305.
          Available to every signed-in user.
        </p>
      </div>

      <form onSubmit={runSearch} className="card space-y-3 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-sm font-medium text-slate-700">Query</label>
            <input
              type="text"
              className="input mt-1 w-full"
              placeholder="designated doctor, impairment rating, network adequacy, ..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Source</label>
            <select
              className="input mt-1"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="">All sources</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Mode</label>
            <select
              className="input mt-1"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
            >
              <option value="hybrid">Hybrid (keyword + semantic)</option>
              <option value="keyword">Keyword only</option>
              <option value="semantic">Semantic only</option>
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={loading || !query.trim()}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </form>

      {hits !== null && (
        <section className="space-y-3" aria-live="polite">
          <h2 className="text-sm font-medium text-slate-500">
            {hits.length} result{hits.length === 1 ? "" : "s"}
          </h2>
          {hits.map((h) => (
            <article key={`${h.source_table}:${h.section_id}`} className="card space-y-1 p-4">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-900">
                  {h.citation} — {h.title}
                </h3>
                <span className="text-xs text-slate-500">
                  {h.source_label} · score {h.score.toFixed(3)}
                </span>
              </div>
              <p
                className="text-sm text-slate-700"
                // The snippet contains backend-supplied <mark> markers; the
                // server-side ts_headline output is HTML-safe (escapes the
                // section text before wrapping in <mark>). This is the only
                // case where the UI trusts server-injected markup.
                dangerouslySetInnerHTML={{ __html: h.snippet }}
              />
              <p className="text-xs">
                {h.source_url && h.source_url !== `fixture://${h.section_id}` ? (
                  <a
                    href={h.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-700 hover:underline"
                  >
                    Open upstream source ↗
                  </a>
                ) : (
                  <span className="text-slate-400">fixture corpus row</span>
                )}
              </p>
            </article>
          ))}
          {hits.length === 0 && (
            <p className="text-sm text-slate-500">
              No matching sections in the corpus. Try a broader query or a different mode.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
